import { focusRing, focusRingOnDark } from '@/design-system';

import { cn } from '../lib/cn';
import { syncLabel, syncTone } from '../offline/sync-engine';
import { useSyncState } from '../offline/useSyncState';

/** `onDark` staat in de navigatierail; `light` op een kaart of in een kopregel. */
export type SyncIndicatorSurface = 'light' | 'onDark';

const TONE_COLOR: Record<SyncIndicatorSurface, Record<'neutral' | 'amber' | 'red', string>> = {
    /*
     * Offline is amber, nooit rood: de inspecteur doet niets fout en zijn werk
     * staat gewoon klaar. Rood is er alleen voor een conflict - dan moet er
     * daadwerkelijk iemand kiezen.
     */
    light: {
        neutral: 'var(--neutral-dot-strong)',
        amber: 'var(--warn)',
        red: 'var(--danger)',
    },
    onDark: {
        neutral: 'var(--rail-muted)',
        amber: 'var(--warn-field)',
        red: 'var(--danger)',
    },
};

export interface SyncIndicatorProps {
    surface?: SyncIndicatorSurface;
    className?: string;
}

/**
 * De enige geanimeerde component in de app: tijdens het synchroniseren pulseert
 * de stip in 2 seconden. Aanklikken probeert de outbox nu meteen te legen.
 */
export function SyncIndicator({ surface = 'light', className }: SyncIndicatorProps) {
    const { state, flushNow } = useSyncState();
    const tone = syncTone(state);
    const isSyncing = state.status === 'syncing';
    const onDark = surface === 'onDark';

    return (
        <button
            type="button"
            onClick={() => void flushNow()}
            title={state.lastError ?? undefined}
            aria-live="polite"
            className={cn(
                'sk-touch-target inline-flex items-center gap-2 rounded-[var(--radius-control)] px-3 text-left',
                'font-[family-name:var(--font-body)] cursor-pointer',
                onDark && 'text-[13px] text-[var(--rail-muted)] hover:bg-[var(--rail-item-hover)]',
                onDark ? focusRingOnDark : focusRing,
                className,
            )}
            style={
                onDark
                    ? undefined
                    : { color: 'var(--text-secondary)', fontSize: 'var(--type-body-size)' }
            }
        >
            <span
                aria-hidden="true"
                className="shrink-0"
                style={{
                    inlineSize: 10,
                    blockSize: 10,
                    borderRadius: '50%',
                    background: TONE_COLOR[surface][tone],
                    animation: isSyncing ? 'var(--animate-skpulse)' : undefined,
                }}
            />
            <span className={onDark ? 'min-w-0 truncate' : undefined}>{syncLabel(state)}</span>
        </button>
    );
}
